import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Home } from 'lucide-react';

const Breadcrumbs: React.FC = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);

  // Check if first segment is language
  const firstSegment = pathnames[0];
  const hasLangPrefix = ['en', 'de', 'fr'].includes(firstSegment);
  
  // Real content paths start after language if present
  const contentPaths = hasLangPrefix ? pathnames.slice(1) : pathnames;
  
  if (contentPaths.length === 0) return null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://beatenberg-resortspa.ch"
      },
      ...contentPaths.map((name, index) => {
        // Construct full path including language prefix if exists
        const pathSegments = hasLangPrefix 
          ? [firstSegment, ...contentPaths.slice(0, index + 1)]
          : contentPaths.slice(0, index + 1);
        const url = `https://beatenberg-resortspa.ch/${pathSegments.join('/')}`;
        
        return {
          "@type": "ListItem",
          "position": index + 2,
          "name": name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' '),
          "item": url
        };
      })
    ]
  };

  return (
    <nav aria-label="Breadcrumb" className="mb-6">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <ol className="flex flex-wrap items-center gap-2 text-sm text-white/90 font-medium tracking-wide">
        <li>
          <Link to={hasLangPrefix ? `/${firstSegment}` : '/'} className="hover:text-alpine-gold transition-colors flex items-center">
             <Home className="w-4 h-4" />
             <span className="sr-only">Home</span>
          </Link>
        </li>
        {contentPaths.map((name, index) => {
          // Construct route path
          const pathSegments = hasLangPrefix 
             ? [firstSegment, ...contentPaths.slice(0, index + 1)]
             : contentPaths.slice(0, index + 1);
          const routeTo = `/${pathSegments.join('/')}`;
          
          const isLast = index === contentPaths.length - 1;
          const formattedName = name.replace(/-/g, ' ').replace(/(^\w|\s\w)/g, m => m.toUpperCase());

          return (
            <li key={name} className="flex items-center gap-2">
              <ChevronRight className="w-3 h-3 opacity-70" />
              {isLast ? (
                <span className="border-b border-alpine-gold pb-0.5 pointer-events-none" aria-current="page">
                  {formattedName}
                </span>
              ) : (
                <Link to={routeTo} className="hover:text-alpine-gold transition-colors">
                  {formattedName}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default Breadcrumbs;
